import DxReportDesigner from 'devexpress-reporting-react/dx-report-designer';
import React, { useRef } from 'react';
import RequestOptions from 'devexpress-reporting-react/dx-report-designer/options/RequestOptions';
import Callbacks from 'devexpress-reporting-react/dx-report-designer/options/Callbacks';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryParamInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import { StringParam } from 'react-router-url-params';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { QueryFactory } from '@lmt/shared-types';
import { UniStorageFactory } from '../../helpers/uni-storage';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import './reporting-dx.css';
import {
  NavigateTab,
  useReactReporting,
  type ReportViewModel,
} from '../../common/devexpress/Reporting/dx-react-reporting';
import { getExtensionValue, setExtensionValue } from './statistics-helpers';

const StatisticsDesigner: React.FC = () => {
  useReactReporting();
  const designerRef = useRef<any>(null);
  const pendingDeletionRef = useRef<string | null>(null);
  const queryClient = useQueryClient();
  const [reportUrl, setReportUrl] = useQueryParamInTab('report', StringParam);
  const i18n = useScopedTranslation('reporting.designer');
  const modals = useModal();

  return (
    <div className="statistics-designer" style={{ height: '100%' }}>
      <DxReportDesigner
        ref={designerRef}
        reportUrl={reportUrl || 'DefaultReport'}
        width="100%"
        height="100%"
      >
        <RequestOptions
          host={UniStorageFactory.UniStorage.BackendUri.replace(/\/?$/, '/')}
          getDesignerModelAction="/api/statistics/designer-model"
        />

        <Callbacks
          ReportSaving={({
            args,
          }: {
            args: { Report: ReportViewModel; Url: string; Cancel: boolean };
          }) => {
            const name =
              (args.Url as string)?.trim() ||
              (args.Report as any)?.displayName?.()?.trim() ||
              '';

            if (name.length === 0) {
              modals.showError({
                text: i18n.t('empty_name_or_only_spaces'),
              });
              args.Cancel = true;
            }
            // console.log(
            //   'ReportSaving',
            //   getExtensionValue(args.Report.extensions, 'OriginalFileName'),
            //   args.Url,
            //   args.Report,
            // );
          }}
          ReportSaved={({
            args,
          }: {
            args: { Report: ReportViewModel; Url: string; Cancel: boolean };
          }) => {
            setExtensionValue(
              args.Report.extensions,
              'OriginalFileName',
              args.Url,
            );
          }}
          ReportOpened={({
            args,
          }: {
            args: { Report: ReportViewModel; Url: string };
          }) => {
            setReportUrl(args?.Url);
          }}
          TabChanged={({
            args,
          }: {
            args: {
              Tab: NavigateTab;
            };
          }) => {
            // console.log('TabChanged', args);
            const url = args?.Tab?.url();
            setReportUrl(url);
            setExtensionValue(
              args.Tab.report().extensions,
              'OriginalFileName',
              url,
            );
          }}
          CustomizeMenuActions={(a) => {
            a.args.Actions?.find((a: any) => a.id === 'dxrd-exit') &&
              (a.args.Actions.find((a: any) => a.id === 'dxrd-exit').visible =
                false);

            a.args.Actions?.push({
              id: 'delete-report',
              container: 'menu',
              text: i18n.t('delete_button_title'),
              imageClassName: 'dxrd-image-delete',
              imageTemplateName: 'dxrd-svg-toolbar-delete',
              visible: true,
              disabled: false,
              clickAction: () => {
                const inst = designerRef.current!.instance();
                pendingDeletionRef.current = inst.GetCurrentTab()?.url();

                modals
                  .showDeletionConfirmation({
                    text: i18n.t('confirm_deletion_text'),
                  })
                  .then(async (isConfirmed) => {
                    if (!isConfirmed || !pendingDeletionRef.current) return;

                    const nameToDelete = pendingDeletionRef.current;
                    pendingDeletionRef.current = null;

                    await QueryFactory.StatisticsViewerClient.deleteReport(
                      nameToDelete,
                    );

                    const currentTab = inst.GetCurrentTab()?.url();
                    if (currentTab === nameToDelete) {
                      inst.CloseCurrentTab();
                    }

                    await queryClient.invalidateQueries({
                      queryKey:
                        QueryFactory.StatisticsViewerQuery.getReportsQueryKey(),
                    });
                  });
              },
            });
          }}
        />
      </DxReportDesigner>
    </div>
  );
};

export default StatisticsDesigner;
